import { Fragment, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

export default function BaseCombobox({
  name,
  list = [],
  currentValue = '',
  defaultLabel = '',
  onValueChange,
  compact = false,
  required = false,
  error = false,
  className = '',
}) {
  const selectedItem = useMemo(
    () => list.find((item) => String(item.key) === String(currentValue)) || null,
    [currentValue, list]
  );

  const triggerPadding = compact ? 'px-3 py-3 pe-9' : 'px-4 py-3 pe-10';
  const triggerIconPosition = compact ? 'end-2.5' : 'end-3';
  const optionPadding = compact ? 'px-3 py-3 pe-8' : 'px-4 py-3 pe-9';

  return (
    <div className={`relative ${className}`}>
      <Listbox value={currentValue} onChange={onValueChange}>
        {({ open }) => (
          <>
            <Listbox.Button
              aria-invalid={error || undefined}
              className={`relative w-full bg-white text-left text-[15px] text-text_secondary transition focus:outline-none ${error ? 'ring-2 ring-red-500 focus:ring-2 focus:ring-red-500' : 'ring-1 ring-[#aab2bf] focus:ring-2 focus:ring-text_tertiary'} ${triggerPadding}`}
            >
              <span
                className={`block truncate ${
                  selectedItem ? 'text-text_secondary' : 'text-[#b2bac7]'
                }`}
              >
                {selectedItem?.label || defaultLabel}
              </span>

              <span
                className={`pointer-events-none absolute inset-y-0 ${triggerIconPosition} flex items-center text-bg_primary transition-transform duration-200 ${
                  open ? 'rotate-180' : ''
                }`}
              >
                <ChevronDownIcon className="h-5 w-5" />
              </span>
            </Listbox.Button>

            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-30 mt-1 max-h-64 w-full overflow-auto bg-white py-1 shadow-[0_14px_30px_rgba(31,41,55,0.16)] ring-1 ring-[#aab2bf] focus:outline-none">
                {list.map((item) => (
                  <Listbox.Option
                    key={item.key}
                    value={item.key}
                    className={({ active }) =>
                      `relative cursor-pointer select-none text-[15px] ${
                        active
                          ? 'bg-[#f3f5f8] text-text_primary'
                          : 'text-text_secondary'
                      } ${optionPadding}`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span className={`block truncate ${selected ? 'font-semibold' : ''}`}>
                          {item.label}
                        </span>

                        <span className="absolute inset-y-0 end-3 flex items-center">
                          <CheckIcon
                            className={`h-4 w-4 ${
                              selected ? 'text-bg_primary opacity-100' : 'opacity-0'
                            }`}
                          />
                        </span>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>

      <select
        name={name}
        value={currentValue}
        required={required}
        onChange={() => {}}
        tabIndex={-1}
        aria-invalid={error || undefined}
        aria-hidden="true"
        className="pointer-events-none absolute h-0 w-0 opacity-0"
      >
        <option value="">{defaultLabel}</option>
        {list.map((item) => (
          <option key={`${name}-${item.key}`} value={item.key}>
            {item.label}
          </option>
        ))}
      </select>
    </div>
  );
}
